import React from "react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiClock,
  FiUser,
} from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import { blogPosts, getBlogPostBySlug } from "./blogData";

function BlogArticle() {
  const { slug } = useParams();
  const article = getBlogPostBySlug(slug);
  const relatedArticles = blogPosts
    .filter((post) => post.slug !== slug)
    .slice(0, 3);
  const galleryImages =
    article?.galleryImages?.slice(0, 4) ??
    Array.from({ length: 4 }, () => article?.image).filter(Boolean);

  if (!article) {
    return (
      <div className="min-h-[100%] bg-body px-4 py-8 sm:px-6 lg:py-12">
        <div className="mx-auto max-w-4xl rounded-[30px] border border-neutral-border bg-neutral-white p-8 text-center shadow-[0_18px_55px_rgba(16,42,107,0.08)]">
          <div className="text-xs font-semibold uppercase tracking-[0.3em] text-primary-default">
            Blog Insight
          </div>
          <h1 className="mt-4 text-3xl font-bold text-primary-dark">
            Article not found
          </h1>
          <p className="mt-4 text-sm leading-7 text-neutral-textSecondary sm:text-base">
            The article you tried to open is not available right now.
          </p>
          <Link
            to="/blog"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-default px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-light"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100%] overflow-hidden bg-body animate-[articleFade_0.5s_ease-out]">
      <div className="px-4 py-6 sm:px-6 lg:py-10">
        <div className="mx-auto max-w-6xl">
          <section className="rounded-[34px] border border-primary-default/10 bg-neutral-white p-6 shadow-[0_22px_65px_rgba(16,42,107,0.08)] sm:p-8 lg:p-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-medium text-neutral-textSecondary transition hover:text-primary-default"
            >
              <FiArrowLeft className="h-4 w-4" />
              Back to insights
            </Link>

            <div className="mt-6 grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
              <div className="space-y-5">
                <div className="inline-flex rounded-full border border-accent-gold/30 bg-accent-gold/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-primary-default">
                  {article.category}
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-textSecondary">
                  <span className="inline-flex items-center gap-2">
                    <FiCalendar className="h-4 w-4 text-primary-default" />
                    {article.publishedAt}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <FiClock className="h-4 w-4 text-primary-default" />
                    {article.readTime}
                  </span>
                </div>

                <h1 className="max-w-3xl text-3xl font-bold leading-tight text-primary-dark sm:text-4xl lg:text-[3.1rem]">
                  {article.title}
                </h1>

                <p className="max-w-2xl text-base leading-8 text-neutral-textSecondary sm:text-lg">
                  {article.excerpt}
                </p>

                <div className="flex items-center gap-4 rounded-[24px] border border-neutral-border bg-neutral-light px-5 py-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-default text-white">
                    <FiUser className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-primary-dark">
                      {article.author}
                    </div>
                    <div className="text-sm text-neutral-textSecondary">
                      {article.authorRole}
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-[30px] border border-primary-default/10 bg-gradient-to-br from-primary-dark via-primary-default to-primary-light p-5 shadow-[0_22px_60px_rgba(16,42,107,0.2)]">
                <div className="grid gap-4 sm:grid-cols-2">
                  {galleryImages.map((image, index) => (
                    <div
                      key={`${article.slug}-${index}`}
                      className="overflow-hidden rounded-[24px] border border-white/10 bg-white/10 shadow-[0_10px_24px_rgba(0,0,0,0.18)]"
                    >
                      <img
                        src={image}
                        alt={`${article.title} ${index + 1}`}
                        className="h-[150px] w-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mt-8 rounded-[34px] border border-neutral-border bg-neutral-white p-6 shadow-[0_18px_55px_rgba(16,42,107,0.06)] sm:p-8 lg:px-12 lg:py-10">
            <div className="mx-auto max-w-4xl">
              <p className="text-lg leading-9 text-neutral-textSecondary first-letter:mr-2 first-letter:text-5xl first-letter:font-bold first-letter:text-primary-dark first-letter:float-left sm:text-xl">
                {article.intro}
              </p>

              {article.sections.map((section) => (
                <div key={section.heading} className="mt-10">
                  <h2 className="text-2xl font-bold text-primary-dark">
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p
                        key={paragraph}
                        className="text-sm leading-8 text-neutral-textSecondary sm:text-base"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              ))}

              <blockquote className="mt-10 rounded-[26px] border border-accent-gold/25 bg-accent-gold/10 px-6 py-5 text-lg italic leading-8 text-primary-dark">
                {article.quote}
              </blockquote>

              <div className="mt-10">
                <h2 className="text-2xl font-bold text-primary-dark">
                  {article.bulletTitle}
                </h2>
                <div className="mt-5 space-y-4">
                  {article.bullets.map((bullet) => (
                    <div key={bullet} className="flex gap-3">
                      <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-accent-gold" />
                      <p className="text-sm leading-8 text-neutral-textSecondary sm:text-base">
                        {bullet}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <h2 className="text-2xl font-bold text-primary-dark">
                  Conclusion
                </h2>
                <p className="mt-4 text-sm leading-8 text-neutral-textSecondary sm:text-base">
                  {article.conclusion}
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-primary-default/10 bg-neutral-light px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary-default"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-8 grid gap-5 lg:grid-cols-[0.92fr_1.08fr]">
            <article className="rounded-[30px] border border-neutral-border bg-neutral-white p-6 shadow-[0_18px_55px_rgba(16,42,107,0.06)]">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-default text-lg font-semibold text-white">
                {article.author
                  .split(" ")
                  .map((word) => word[0])
                  .join("")
                  .slice(0, 2)}
              </div>
              <h3 className="mt-5 text-xl font-bold text-primary-dark">
                About {article.author}
              </h3>
              <p className="mt-3 text-sm leading-8 text-neutral-textSecondary">
                {article.authorBio}
              </p>
            </article>

            <article className="rounded-[30px] border border-primary-default/10 bg-gradient-to-br from-primary-dark via-primary-default to-primary-light p-6 text-white shadow-[0_24px_65px_rgba(16,42,107,0.2)]">
              <div className="inline-flex rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-accent-gold">
                More from WFS
              </div>
              <h3 className="mt-5 text-2xl font-bold">
                Explore the latest blogs
              </h3>
              <p className="mt-4 max-w-xl text-sm leading-8 text-white/78 sm:text-base">
                Continue reading professional insights on tax planning, cash
                flow, compliance discipline, and growth strategy.
              </p>

              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {relatedArticles.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="rounded-[22px] border border-white/10 bg-white/10 p-4 transition hover:bg-white/15"
                  >
                    <div className="text-xs font-semibold uppercase tracking-[0.24em] text-accent-gold">
                      {post.category}
                    </div>
                    <h4 className="mt-3 text-base font-semibold leading-7 text-white">
                      {post.title}
                    </h4>
                  </Link>
                ))}
              </div>

              <Link
                to="/blog"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-accent-gold px-5 py-3 text-sm font-semibold text-primary-dark transition hover:bg-accent-goldLight"
              >
                Explore more articles
                <FiArrowRight className="h-4 w-4" />
              </Link>
            </article>
          </section>
        </div>
      </div>

      <style>
        {`
          @keyframes articleFade {
            from {
              opacity: 0;
              transform: translateY(14px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
}

export default BlogArticle;
